/**
 * What a `timedEvent` rule does when its time comes (10 §3). The world agrees with the text:
 * things that leave are removed (`removeProp`), things that arrive appear (`wakeTagged`).
 * Every effect first wakes the dormant spawns tagged for it; what else it does is below.
 */

import { raiseAlert, sendToInvestigate } from '../alert';
import { type Ctx, isFighter } from '../ctx';
import { destroyProp, explode, removeProp, woundRanger } from '../damage';
import { isAutomated } from '../enemies/specials/kinds';
import { PROPS } from '../props';
import { EXPANSION, OSPREY } from '../tuning';
import type { Enemy, ObjectiveRule, PropDef } from '../types';
import { dist, failObjective, propDef, propDestroyed, targetProps } from './common';
import { partyStaysHome, wakeTagged } from './dormant';

export type TimedRule = Extract<ObjectiveRule, { type: 'timedEvent' }>;

/** Surviving targets drive, steam or fly away. Lost unless enough of them were already wrecked. */
export function targetsLeave(ctx: Ctx, reason: string | undefined): void {
  const { world: w, statics } = ctx;
  for (const target of targetProps(statics.def)) if (!propDestroyed(w, target.id)) removeProp(ctx, target.id);
  if (!w.objective.complete) failObjective(ctx, reason ?? 'THE TARGETS GOT AWAY');
}

/** Can this event still change anything? If not it is spent quietly, and the HUD stops counting it down. */
export function timedStillApplies(ctx: Ctx, rule: TimedRule): boolean {
  const { world: w, statics } = ctx;
  // The man it would have brought was warned off: neither he nor his escort sets out, and nothing is announced.
  if (partyStaysHome(ctx, rule.wakes ?? rule.effect)) return false;
  switch (rule.effect) {
    case 'targetsLeave':
      return targetProps(statics.def).some((t) => !propDestroyed(w, t.id));
    case 'contactArrives':
      return w.objective.captives.some((c) => c.state === 'absent');
    case 'ospreyArrives':
      return w.osprey.phase === 'away';
    default:
      return true;
  }
}

/** Where a search party, or a column with no route of its own, makes for: what the ranger is after. */
function focus(ctx: Ctx): { x: number; z: number } {
  const { world: w, statics } = ctx;
  const hidden = w.objective.captives.find((c) => c.state === 'held' || c.state === 'waiting');
  if (hidden) return { x: hidden.x, z: hidden.z };
  const target = targetProps(statics.def).find((t) => !propDestroyed(w, t.id));
  if (target) return { x: target.x, z: target.z };
  const zone = statics.def.objective.zone ?? statics.def.objective.zones?.[0] ?? statics.def.goalZone;
  return { x: (zone.x0 + zone.x1) / 2, z: (zone.z0 + zone.z1) / 2 };
}

/** The props a strike lands on: the rule's list, else the point to be clear of, else the targets. */
function strikePoints(ctx: Ctx, rule: TimedRule): PropDef[] {
  const def = ctx.statics.def;
  const ids = rule.strikePropIds
    ?? (def.objective.clearFromPropId !== undefined ? [def.objective.clearFromPropId] : def.objective.targetPropIds);
  const out: PropDef[] = [];
  for (const id of ids) {
    const prop = propDef(def, id);
    if (prop) out.push(prop);
  }
  return out;
}

function strike(ctx: Ctx, rule: TimedRule): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const points = strikePoints(ctx, rule);
  const clear = statics.def.objective.clearDistance ?? 0;
  // Inside the danger area he goes down where he stands; inside a blast itself, `explode` finishes it.
  const caught = points.some((p) => dist(p.x, p.z, r.x, r.z) < clear);
  if (caught && (r.status === 'active' || r.status === 'boarding')) woundRanger(ctx, 3, 'blast');
  for (const p of points) {
    // He called it in: what it kills is his.
    explode(ctx, p.x, p.z, rule.strikeRadius ?? EXPANSION.strikeRadius, 'bomb', true);
    if (PROPS[p.kind].durability !== 'none') destroyProp(ctx, p.id);
  }
}

function alarmAtStart(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const { x, z } = statics.def.player;
  raiseAlert(ctx, 3, x, z);
  for (const e of w.enemies) if (isFighter(e) && !isAutomated(e.kind)) sendToInvestigate(e, x, z, true);
}

function ospreyComes(ctx: Ctx): void {
  const osprey = ctx.world.osprey;
  if (osprey.phase !== 'away') return;
  osprey.phase = 'inbound';
  osprey.ticks = OSPREY.inboundTicks;
  osprey.recalled = true;
  ctx.events.push({ type: 'osprey', phase: 'inbound' });
}

export function fireTimed(ctx: Ctx, rule: TimedRule): void {
  const woken: Enemy[] = wakeTagged(ctx, rule.wakes ?? rule.effect);
  switch (rule.effect) {
    case 'targetsLeave':
      targetsLeave(ctx, rule.reason);
      break;
    case 'convoyCrosses':
      break;
    case 'searchPartyArrives': {
      const at = focus(ctx);
      for (const e of woken) {
        e.wary = true;
        sendToInvestigate(e, at.x, at.z, true);
      }
      break;
    }
    case 'contactArrives':
      for (const c of ctx.world.objective.captives) if (c.state === 'absent') c.state = 'waiting';
      break;
    case 'strikeLands':
      strike(ctx, rule);
      break;
    case 'cellFoundEmpty':
      alarmAtStart(ctx);
      break;
    case 'ospreyArrives':
      ospreyComes(ctx);
      break;
  }
}
